/**
 * WebSocket 配置和工具函数
 * 统一管理WebSocket连接，支持开发和生产环境
 */

import config from '../config';

// 获取WebSocket基础URL
const getWebSocketBaseUrl = () => {
  return config.api.wsURL;
};

// 获取数据处理WebSocket URL
export const getDataProcessingWebSocketUrl = (userId, token) => {
  const baseUrl = getWebSocketBaseUrl();
  
  if (!userId) {
    console.warn('WebSocket连接需要用户ID');
    return null;
  }
  
  if (!token) {
    console.warn('WebSocket连接需要认证token');
    return null;
  }
  
  // 必须带 token：后端以 token 用户为准，防止串台到路径中的 userId
  const q = new URLSearchParams({ token: String(token) });
  return `${baseUrl}/data-processing/ws/${userId}?${q.toString()}`;
};

// 获取通用WebSocket URL（用于测试）
export const getTestWebSocketUrl = () => {
  const baseUrl = getWebSocketBaseUrl();
  return `${baseUrl}/data-processing/ws/test`;
};

// WebSocket连接管理器
export class WebSocketManager {
  constructor(url, options = {}) {
    this.url = url;
    this.options = {
      reconnectInterval: 3000,
      maxReconnectAttempts: 5,
      connectionTimeout: 5000,
      ...options
    };
    
    this.ws = null;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.connectionTimer = null;
    this.heartbeatTimer = null;
    this.isConnecting = false;
    this.isManualClose = false;
    
    // 事件回调
    this.onOpen = null;
    this.onMessage = null;
    this.onClose = null;
    this.onError = null;
    this.onReconnect = null;
  }
  
  connect() {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }
    
    if (!this.url) {
      console.error('WebSocket URL未设置');
      return;
    }
    
    this.isConnecting = true;
    this.isManualClose = false;
    
    try {
      this.ws = new WebSocket(this.url);
      
      // 设置连接超时
      this.connectionTimer = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close();
          this.handleConnectionTimeout();
        }
      }, this.options.connectionTimeout);
      
      this.ws.onopen = (event) => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        
        if (this.connectionTimer) {
          clearTimeout(this.connectionTimer);
          this.connectionTimer = null;
        }
        
        console.log('WebSocket连接已建立:', this.url);
        
        // 延迟启动心跳，给连接一些稳定时间
        setTimeout(() => {
          if (this.isConnected()) {
            this.startHeartbeat();
          }
        }, 5000);
        
        if (this.onOpen) {
          this.onOpen(event);
        }
      };
      
      this.ws.onmessage = (event) => {
        try {
          // 先尝试解析JSON
          const data = JSON.parse(event.data);
          
          // 处理心跳响应
          if (data.type === 'pong' || data.type === 'heartbeat') {
            console.log('收到心跳响应');
            return;
          }
          
          // 处理其他消息
          if (this.onMessage) {
            this.onMessage(event);
          }
        } catch (error) {
          // 如果不是JSON格式，检查是否是简单的pong响应
          if (event.data === 'pong') {
            console.log('收到简单心跳响应');
            return;
          }
          
          // 其他非JSON消息直接传递给处理函数
          if (this.onMessage) {
            this.onMessage(event);
          }
        }
      };
      
      this.ws.onclose = (event) => {
        this.isConnecting = false;
        
        // 停止心跳
        this.stopHeartbeat();
        
        if (this.connectionTimer) {
          clearTimeout(this.connectionTimer);
          this.connectionTimer = null;
        }
        
        console.log('WebSocket连接已断开');
        
        if (this.onClose) {
          this.onClose(event);
        }
        
        // 如果不是手动关闭且连接时间超过5秒才重连，避免立即重连
        if (!this.isManualClose && this.reconnectAttempts < this.options.maxReconnectAttempts) {
          // 添加延迟，避免立即重连
          setTimeout(() => {
            if (!this.isManualClose) {
              this.scheduleReconnect();
            }
          }, 1000);
        }
      };
      
      this.ws.onerror = (event) => {
        this.isConnecting = false;
        
        console.error('WebSocket错误:', event);
        
        if (this.onError) {
          this.onError(event);
        }
      };
      
    } catch (error) {
      this.isConnecting = false;
      console.error('创建WebSocket连接失败:', error);
      
      if (this.onError) {
        this.onError(error);
      }
    }
  }
  
  handleConnectionTimeout() {
    this.isConnecting = false;
    console.warn('WebSocket连接超时');
    
    if (this.onError) {
      this.onError(new Error('连接超时'));
    }
    
    // 尝试重连
    if (this.reconnectAttempts < this.options.maxReconnectAttempts) {
      this.scheduleReconnect();
    }
  }
  
  scheduleReconnect() {
    if (this.reconnectTimer) {
      return;
    }
    
    this.reconnectAttempts++;
    
    console.log(`正在尝试重新连接... (${this.reconnectAttempts}/${this.options.maxReconnectAttempts})`);
    
    if (this.onReconnect) {
      this.onReconnect(this.reconnectAttempts);
    }
    
    // 使用指数退避算法，避免频繁重连
    const backoffDelay = Math.min(this.options.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, backoffDelay);
  }
  
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(typeof data === 'string' ? data : JSON.stringify(data));
      return true;
    } else {
      console.warn('WebSocket未连接，无法发送消息');
      return false;
    }
  }
  
  close() {
    this.isManualClose = true;
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.connectionTimer) {
      clearTimeout(this.connectionTimer);
      this.connectionTimer = null;
    }
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
  
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
  
  getReadyState() {
    return this.ws ? this.ws.readyState : WebSocket.CLOSED;
  }
  
  // 启动心跳
  startHeartbeat() {
    this.stopHeartbeat(); // 先停止之前的心跳
    
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected()) {
        this.send('ping'); // 发送简单的ping字符串
      } else {
        this.stopHeartbeat();
      }
    }, 45000); // 每45秒发送一次心跳，减少频率
  }
  
  // 停止心跳
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}

// 导出默认配置
export const defaultWebSocketOptions = {
  reconnectInterval: config.websocket.reconnectInterval,
  maxReconnectAttempts: config.websocket.maxReconnectAttempts,
  connectionTimeout: config.websocket.connectionTimeout
};

export default {
  getDataProcessingWebSocketUrl,
  getTestWebSocketUrl,
  WebSocketManager,
  defaultWebSocketOptions
};